import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { useAuth } from '../auth/AuthContext';
import { ApiError, api } from '../lib/api';
import { Alert, Badge, Button, Card, CopyButton, Label, TextInput } from '../components/ui';
import { useToast } from '../components/toast';

/** QR-Code als Canvas (CSP-sicher, keine externen Bilder). */
function QrCanvas({ value }: { value: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (ref.current) void QRCode.toCanvas(ref.current, value, { width: 192, margin: 1 });
  }, [value]);
  return <canvas ref={ref} className="rounded-md border border-slate-200 bg-white dark:border-slate-600" />;
}

type WizardState =
  | { phase: 'idle' }
  | { phase: 'setup'; secret: string; otpauthUrl: string }
  | { phase: 'recovery'; codes: string[] };

export default function SecurityPage() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const toast = useToast();
  const [wizard, setWizard] = useState<WizardState>({ phase: 'idle' });
  const [confirmCode, setConfirmCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const startSetup = () =>
    void run(async () => {
      const { secret, otpauthUrl } = await api.mfaSetup();
      setConfirmCode('');
      setWizard({ phase: 'setup', secret, otpauthUrl });
    });

  const activate = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { recoveryCodes } = await api.mfaEnable(confirmCode.trim());
      setWizard({ phase: 'recovery', codes: recoveryCodes });
    });
  };

  const finishRecovery = () =>
    void run(async () => {
      await refresh();
      setWizard({ phase: 'idle' });
      toast.success(t('security.enabled'));
    });

  // Recovery-Codes in einem eigenen Fenster zum Ausdrucken/als PDF speichern.
  const printCodes = (codes: string[]) => {
    const win = window.open('', '_blank', 'width=480,height=640');
    if (!win) return;
    const items = codes.map((c) => `<li>${c}</li>`).join('');
    win.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>${t('security.recoveryTitle')}</title>` +
        '<style>body{font-family:system-ui,sans-serif;padding:32px;color:#0f172a}' +
        'h1{font-size:18px;margin:0 0 8px}p{font-size:13px;color:#475569}' +
        'ol{font-family:ui-monospace,monospace;font-size:15px;line-height:2;columns:2;margin-top:16px}</style>' +
        `</head><body><h1>${t('app.name')} — ${t('security.recoveryTitle')}</h1>` +
        `<p>${t('security.recoveryHint')}</p><ol>${items}</ol></body></html>`,
    );
    win.document.close();
    win.focus();
    win.print();
  };

  const disable = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.mfaDisable(disablePassword);
      setDisablePassword('');
      await refresh();
      toast.success(t('security.disabled'));
    });
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-10">
      <h1 className="text-2xl font-bold tracking-tight">{t('security.title')}</h1>

      {user.mfaSetupRequired && (
        <div className="mt-4">
          <Alert kind="info">{t('security.requiredHint')}</Alert>
        </div>
      )}
      {error && (
        <div className="mt-4">
          <Alert kind="error">{error}</Alert>
        </div>
      )}

      <Card className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('security.mfaTitle')}</h2>
          {user.totpEnabled ? (
            <Badge tone="green">{t('security.statusEnabled')}</Badge>
          ) : (
            <Badge tone="amber">{t('security.statusDisabled')}</Badge>
          )}
        </div>

        {/* Einrichtung */}
        {!user.totpEnabled && wizard.phase === 'idle' && (
          <div className="mt-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">{t('security.intro')}</p>
            <Button className="mt-3" disabled={busy} onClick={startSetup}>
              {busy ? t('common.pleaseWait') : t('security.start')}
            </Button>
          </div>
        )}

        {wizard.phase === 'setup' && (
          <form onSubmit={activate} className="mt-4 flex flex-col gap-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">{t('security.scanHint')}</p>
            <QrCanvas value={wizard.otpauthUrl} />
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-300">{t('security.manualHint')}</p>
              <div className="mt-1 flex items-center gap-2">
                <code className="rounded bg-slate-100 px-2 py-1 font-mono text-sm dark:bg-slate-700">
                  {wizard.secret}
                </code>
                <CopyButton value={wizard.secret} />
              </div>
            </div>
            <div>
              <Label htmlFor="mfa-confirm">{t('security.confirmLabel')}</Label>
              <TextInput
                id="mfa-confirm"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                autoComplete="one-time-code"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                className="max-w-[10rem]"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy}>
                {busy ? t('common.pleaseWait') : t('security.activate')}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setWizard({ phase: 'idle' })}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}

        {wizard.phase === 'recovery' && (
          <div className="mt-4 flex flex-col gap-4">
            <h3 className="font-semibold">{t('security.recoveryTitle')}</h3>
            <Alert kind="info">{t('security.recoveryHint')}</Alert>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-md bg-slate-100 p-4 font-mono text-sm dark:bg-slate-700">
              {wizard.codes.map((code) => (
                <span key={code}>{code}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <CopyButton value={wizard.codes.join('\n')} className="px-3.5 py-2 text-sm" />
              <Button type="button" variant="secondary" onClick={() => printCodes(wizard.codes)}>
                {t('security.printCodes')}
              </Button>
              <Button disabled={busy} onClick={finishRecovery}>
                {t('security.recoveryConfirm')}
              </Button>
            </div>
          </div>
        )}

        {/* Deaktivieren */}
        {user.totpEnabled && !user.mfaRequired && (
          <form onSubmit={disable} className="mt-6 border-t border-slate-200 pt-4 dark:border-slate-700">
            <h3 className="font-semibold">{t('security.disable')}</h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t('security.disableHint')}</p>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div>
                <Label htmlFor="mfa-disable-pw">{t('auth.password')}</Label>
                <TextInput
                  id="mfa-disable-pw"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                />
              </div>
              <Button type="submit" variant="danger" disabled={busy}>
                {busy ? t('common.pleaseWait') : t('security.disable')}
              </Button>
            </div>
          </form>
        )}
        {user.totpEnabled && user.mfaRequired && (
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t('security.enforcedHint')}</p>
        )}
      </Card>
    </div>
  );
}
